import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Speaker } from "../interfaces/home/IMainContainer";

interface EditSpeakerModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (text: string) => Promise<void>;
  speaker: Speaker | null;
  isLoading?: boolean;
}

const EditSpeakerModal: React.FC<EditSpeakerModalProps> = ({
  open,
  onClose,
  onSave,
  speaker,
  isLoading = false,
}) => {
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<{
    text?: string;
  }>({});

  useEffect(() => {
    if (open && speaker) {
      setText(speaker.text || "");
      setErrors({});
    }
  }, [open, speaker]);

  const handleSave = async () => {
    const newErrors: { text?: string } = {};

    if (!text.trim()) {
      newErrors.text = "Text is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSave(text.trim());
      onClose();
    } catch (error) {
      console.error("Error saving speaker section:", error);
      // Error is handled by parent component
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Only allow closing via Cancel button
        // Disable backdrop click and ESC key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          zIndex: 100000, // Dialog content above backdrop
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5) !important", // Dark overlay to hide background
            zIndex: 99999, // Backdrop above other content including Floating Edit Summary popup
          },
        },
      }}
      sx={{
        zIndex: 99999, // Dialog container z-index
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5) !important", // Ensure dark backdrop
          zIndex: "99999 !important", // Force backdrop above everything
        },
        "& .MuiDialog-container": {
          zIndex: 99999,
        },
        "& .MuiDialog-paper": {
          zIndex: 100000, // Dialog content above backdrop
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon />
          <Typography variant="h6">Edit Speaker Section</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Text"
            multiline
            rows={6}
            fullWidth
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.text;
                return newErrors;
              });
            }}
            error={!!errors.text}
            helperText={errors.text || "Edit the transcription text for this section"}
            required
            disabled={isLoading}
            placeholder="Enter speaker text here..."
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !text.trim()}
          startIcon={isLoading ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSpeakerModal;

